import { z } from "zod";

// https://cran.r-project.org/doc/manuals/r-release/R-lang.html#Identifiers-1
// > Identifiers consist of a sequence of letters, digits, the period (‘.’) and
// > the underscore. They must not start with a digit or an underscore, or with
// > a period followed by a digit.
// Note: technically the definition of "letter" is locale-dependent, but we
// limit it to ASCII letters for simplicity.

const ID_CHARS_REGEX = /^[\w._]+$/;

// https://cran.r-project.org/doc/manuals/r-release/R-lang.html#Reserved-words-1
const KEYWORDS = new Set([
  "if",
  "else",
  "repeat",
  "while",
  "function",
  "for",
  "in",
  "next",
  "break",
  "TRUE",
  "FALSE",
  "NULL",
  "Inf",
  "NaN",
  "NA",
  "NA_integer_",
  "NA_real_",
  "NA_complex_",
  "NA_character_",
]);
// from above link: `... ..1 ..2 etc.`
const RESERVED_REGEX = /^\.\.(\.|\d+)$/;

export const RIdentifier = z
  .string()
  .brand("RIdentifier")
  // `superRefine`, not `refine`: the runtypes constraint returned a different
  // message per branch, and `refine` has one message for the whole predicate --
  // worse, it treats a returned string as `true`, so the check would not run.
  .superRefine((name, ctx) => {
    const fail = (message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    };

    if (KEYWORDS.has(name)) {
      fail(`'${name}' is a reserved word in R`);
    } else if (!ID_CHARS_REGEX.test(name)) {
      fail(
        `R identifiers must consist of letters, digits, periods, and underscores.`,
      );
    } else if (RESERVED_REGEX.test(name)) {
      fail(`'...', '..1', '..2', etc. are reserved words in R`);
    } else if (/^\d/.test(name)) {
      fail(`R identifiers must not start with a digit`);
    } else if (name.startsWith("_")) {
      fail(`R identifiers must not start with an underscore`);
    } else if (/^\.\d/.test(name)) {
      fail(`R identifiers must not start with a period followed by a digit`);
    }
  });
